import PageHeader from "@ui/components/header/PageHeader";
import { PageContainer, PageContent } from "@ui/components/wraper";
import React, { useEffect, useMemo, useState } from "react";
import { useReportsFilter } from "./hooks/useReportsFilter";
import { useReportsData } from "./hooks/useReportsData";
import { TemperatureTable } from "./components/TemperatureTable";
import { ReportFilters } from "./components/ReportFilters";
import { Alert, Box, CircularProgress, Tab, Tabs, Typography, } from "@mui/material";
import { DEFAULT_REPORT_SENSOR, applicableReportSensors, reportSensorMeta, } from "./reportSensors";
import { useTranslation } from "react-i18next";
const ReportsPage = () => {
    const { t } = useTranslation(["form", "validation", "common"]);
    // The sensor the report is about: one tab each (the internal temperature is the original report).
    const [sensor, setSensor] = useState(DEFAULT_REPORT_SENSOR.sensor);
    const { control, resetFilters, selectedNodes, nodeStatus, interval, showBy, fromDate, toDate, nodeOptions, nodesLoading, handleNodesSearch, handleNodesListboxScroll, loadNextNodesPage, } = useReportsFilter();
    // Tabs follow the selected node types: older node types only have the temperature (no tabs); type 3 nodes add external
    // temperature and humidity; the AP-Node adds humidity and CO2. A tab no selected node supports is not offered.
    // After a search the server's answer for that selection decides (it knows which sensors are switched off).
    const [serverApplicable, setServerApplicable] = useState();
    const applicableSensors = useMemo(() => applicableReportSensors(selectedNodes).filter((meta) => !serverApplicable || serverApplicable.includes(meta.sensor)), [selectedNodes, serverApplicable]);
    const activeSensor = applicableSensors.some((s) => s.sensor === sensor)
        ? sensor
        : DEFAULT_REPORT_SENSOR.sensor;
    const activeMeta = reportSensorMeta(activeSensor);
    const { serverApplicableSensors, temperatureData, reportColumnNodes, excludedNodes, loading, error, downloadExcelReport, downloadPdfReport, refetchData, clearData, } = useReportsData({
        selectedNodes,
        nodeStatus,
        fromDate,
        toDate,
        interval,
        showBy,
        sensor: activeSensor,
    });
    // Hand the server's sensor list (known only after a search) to the tabs above.
    useEffect(() => {
        setServerApplicable(serverApplicableSensors);
    }, [serverApplicableSensors]);
    // Switching the tab re-runs the report that is on screen for the new sensor (nothing to run before the first search).
    useEffect(() => {
        if (reportColumnNodes.length > 0) {
            void refetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSensor]);
    return (React.createElement(PageContainer, null,
        React.createElement(PageHeader, { title: t("common:pages.reports") }),
        React.createElement(PageContent, null,
            React.createElement(ReportFilters, { control: control, nodeOptions: nodeOptions, nodesLoading: nodesLoading, onNodesSearch: handleNodesSearch, onNodesListboxScroll: handleNodesListboxScroll, onNodesNeedMore: loadNextNodesPage, onSearch: refetchData, onClear: () => {
                    resetFilters();
                    clearData();
                }, loading: loading }),
            applicableSensors.length > 1 && (React.createElement(Box, { sx: { flexShrink: 0 } },
                React.createElement(Tabs, { value: activeSensor, onChange: (_, value) => setSensor(value) }, applicableSensors.map((s) => (React.createElement(Tab, { key: s.sensor, value: s.sensor, label: t(s.labelKey) })))))),
            excludedNodes.length > 0 && (React.createElement(Alert, { severity: "info", sx: { my: 1, flexShrink: 0 } }, t("common:pages.reportsNodesNotShown", {
                sensor: t(activeMeta.labelKey),
                nodes: excludedNodes.join(", "),
            }))),
            error && (React.createElement(Alert, { severity: "error", sx: { mb: 2 } }, error)),
            loading ? (React.createElement(Box, { sx: {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 8,
                } },
                React.createElement(CircularProgress, { size: 40 }),
                React.createElement(Typography, { variant: "body2", color: "text.secondary", sx: { mt: 2 } }, "Loading report data..."))) : (
            /* Table Section */
            React.createElement(TemperatureTable, { selectedNodes: selectedNodes, gridColumnNodes: reportColumnNodes, temperatureData: temperatureData, sensorMeta: activeMeta, noNodeHasSensor: excludedNodes.length > 0 && reportColumnNodes.length === 0, onDownloadExcel: downloadExcelReport, onDownloadPdf: downloadPdfReport })))));
};
export default ReportsPage;
